/**
 * Camada 1 · tela de login, em duas colunas.
 *
 * A TELA E NOSSA, E ESSE E O PONTO. Nenhum componente pronto do Supabase entra aqui: o
 * Supabase cuida de senha, hash, forca bruta, recuperacao, MFA, Google, sessao e refresh, e
 * nada disso e visual. O layout, o texto e a identidade de cada produto sao nossos, e e
 * por isso que trocar o provedor depois nao mexe em uma linha deste arquivo.
 *
 * DUAS COLUNAS ACIMA DE 1024px, UMA COLUNA ABAIXO. Em 390px duas colunas viram duas tiras
 * ilegiveis, entao elas se empilham: o formulario primeiro, a apresentacao logo abaixo. A
 * apresentacao e o que muda entre produtos, e ela vem inteira do ProductConfig: o formulario
 * e identico em todos.
 *
 * DEFEITO MEDIDO EM 30/08/2026, NAO REPETIR: a apresentacao era `hidden ... lg:flex`, e nao
 * empilhada. Abaixo de 1024px o titulo, o subtitulo, os paragrafos, o bloco de IA, os selos e
 * a marca da plataforma NAO EXISTIAM, e sobrava um formulario solto no meio de uma tela vazia,
 * com 370px de branco acima dele em 390px e 500px em 768px. A geometria nao acusava nada: sem
 * estouro horizontal, sem elemento fora da tela e com os alvos de toque corretos. So a captura
 * mostrou, porque o que faltava nao estava fora da tela: nao estava em lugar nenhum.
 */

import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { classificarRetornoOauth } from './retorno-oauth.js';
import { t } from '@complexity/i18n';
import type { ProductConfig } from '@complexity/product-kit';
import {
  Botao,
  Campo,
  ControlesIdiomaTema,
  FaixaAmbiente,
  LogoGoogle,
  Modal,
  icone as resolverIcone,
  useIdiomaVivo,
} from '@complexity/ui';
import { DOMINIO, NOME_CURTO_PARTES, NOME_PARTES, Simbolo, URL_AUTORIA } from '@complexity/brand';
import type { Ambiente } from '@complexity/config';
import type { IdentityProvider, ResultadoLogin } from '../identity-provider.js';
import { validarEmail } from '@complexity/credenciais';

export interface TelaLoginProps {
  readonly produto: ProductConfig;
  readonly provedor: IdentityProvider;
  readonly aoEntrar: (resultado: Extract<ResultadoLogin, { ok: true }>) => void;
  /** Para onde o Google devolve depois do redirecionamento. */
  readonly urlRetorno: string;
  /**
   * O ambiente em que esta tela roda, para a faixa do topo.
   *
   * OPCIONAL, e ausente vale como NAO DECLARADO, que desenha o aviso em vermelho. E o
   * comportamento certo: o que nao pode acontecer e a tela de login ficar sem faixa por
   * um erro de digitacao, porque tela sem faixa parece producao. E aqui pesa mais que em
   * qualquer outra tela, porque e onde a pessoa digita a senha.
   */
  readonly ambiente?: Ambiente;
}

/**
 * Parte o texto e devolve as palavras declaradas com PESO, e o resto como esta.
 *
 * A BUSCA E PELO TEXTO JA TRADUZIDO, e nao pela frase do config: em ingles a palavra e
 * outra, entao procurar "amador" numa frase em ingles nao acharia nada e a enfase sumiria
 * calada. Quem declara a enfase declara a palavra do idioma em que ela aparece, e o
 * dicionario traduz as duas.
 *
 * PALAVRA AUSENTE NAO QUEBRA NADA: ela simplesmente nao pinta. Derrubar a tela de entrada
 * por causa de uma palavra mal escrita num config seria trocar uma enfase que faltou por
 * uma tela que nao abre.
 *
 * A COMPARACAO E EXATA, sem normalizar caixa nem acento. Normalizar faria "Voo" pintar onde
 * o config pediu "voo", e enfase que aparece onde ninguem pediu e pior que enfase de menos.
 */
function comEnfase(texto: string, enfases?: readonly string[]): ReactNode {
  if (!enfases || enfases.length === 0) return texto;

  // AS MAIS LONGAS PRIMEIRO, para "voo executivo" vencer "voo" quando as duas forem
  // declaradas: sem isto, a curta parte a frase no meio e a longa nunca casa.
  const alvos = [...enfases].filter(Boolean).sort((a, b) => b.length - a.length);

  // A EXPRESSAO E MONTADA COM OS TERMOS ESCAPADOS, porque um ponto ou parentese vindo do
  // config viraria metacaractere e casaria o que ninguem pediu.
  const escapado = alvos.map((a) => a.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  const partes = texto.split(new RegExp(`(${escapado})`, 'g'));

  return partes.map((parte, i) =>
    alvos.includes(parte) ? (
      // <strong> E NAO <b>: o peso aqui e semantico, e nao decoracao. Leitor de tela
      // anuncia a enfase, que e justamente o que a palavra destacada quer dizer.
      <strong key={`${parte}-${i}`} className="font-semibold text-texto">
        {parte}
      </strong>
    ) : (
      parte
    ),
  );
}

export function TelaLogin({ produto, provedor, aoEntrar, urlRetorno, ambiente }: TelaLoginProps) {
  /**
   * O CAMPO DE E-MAIL E SENHA APARECE, OU A TELA E SO O GOOGLE.
   *
   * PEDIDO DO FUNDADOR EM 13/09/2026, com a captura do Super Admin: "nessa tela havera
   * apenas login com o google", fechado em seguida como "deixe as duas formas de entrada
   * (google apenas somente em producao)".
   *
   * NESTE REPOSITORIO a decisao e so do manifesto: a conta de trabalho da plataforma, que na
   * plataforma liga a senha em `local` e `hml`, saiu daqui em 19/09/2026 ("cds@cds nao tem nada
   * a ver com esse projeto"). O admin atual oferece Google e senha em producao, e o manifesto
   * do admin nao liga `somenteGoogleEmProducao`, entao as duas formas aparecem sempre.
   * Divergencia protegida (`scripts/sincronizar-chassi.sh`).
   */
  const entradaPorSenha = !produto.login.somenteGoogleEmProducao;

  // A TROCA DE IDIOMA PRECISA REDESENHAR ESTA TELA, e sem esta linha ela nao redesenha.
  //
  // MEDIDO EM 08/09/2026 no admin: clicar EN gravava o cookie, trocava o `lang` do documento
  // para `en-US` e movia o `aria-pressed` do botao. Tudo indicava sucesso, e NENHUM texto
  // mudava: so um reload trazia o ingles.
  //
  // A CAUSA e que `t()` le o dicionario no momento em que o componente RENDERIZA, e trocar
  // o idioma nao renderiza nada por conta propria. O hook assina a troca e devolve um estado
  // novo, que e o que obriga o React a redesenhar com o dicionario do idioma novo.
  //
  // O VALOR NAO E USADO de proposito: o que interessa e o estado mudar. Nomea-lo `_` seria
  // pior, porque esconderia que a chamada TEM efeito e nao pode ser removida.
  useIdiomaVivo();

  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  /**
   * O RETORNO DO OAUTH QUE DEU ERRADO, lido da URL na montagem da tela.
   *
   * -------------------------------------------------------------------------
   * O DEFEITO QUE COMPROU ISTO, medido em 30/08/2026
   * -------------------------------------------------------------------------
   * Ele clicou em "Entrar com o Google", viu a tela de consentimento e apertou CANCELAR.
   * O provedor devolveu `?error=invalid_request&error_code=bad_oauth_state`, e o app NAO
   * LIA esse parametro em lugar nenhum: a pessoa caia numa tela sem login e sem
   * explicacao, achando que o sistema quebrou. Palavras dele: "TEM QUE VOLTAR PARA O
   * LOGIN, CERTO?". Tem.
   *
   * -------------------------------------------------------------------------
   * OS DOIS LUGARES, e ler so um deixa metade dos casos de fora
   * -------------------------------------------------------------------------
   * O provedor devolve o erro ora na QUERY (`?error=...`), ora no FRAGMENTO
   * (`#error=...`), dependendo do fluxo e de onde ele parou. Ler apenas `search` funciona
   * na maioria dos testes e falha justamente no caso que o usuario encontra.
   *
   * -------------------------------------------------------------------------
   * A URL E LIMPA DEPOIS DE LIDA, e isso nao e cosmetico
   * -------------------------------------------------------------------------
   * Sem limpar, o erro sobrevive a um F5 e a pessoa continua vendo a mensagem depois de
   * ja ter entendido. Pior: se ela salvar a pagina nos favoritos, guarda um endereco que
   * sempre abre com erro. `replaceState` troca a URL sem recarregar e sem somar uma
   * entrada no historico, entao o botao Voltar continua fazendo o que a pessoa espera.
   */
  useEffect(() => {
    // `window` pode nao existir em renderizacao de servidor. Sair cedo e mais honesto que
    // um `typeof window` espalhado por cada linha abaixo.
    if (typeof window === 'undefined') return;

    // A REGRA mora em `retorno-oauth.ts`, e nao aqui: la ela e exercitada em
    // milissegundos com os casos de borda escritos a mao, inclusive o fragmento sem `#`.
    const situacao = classificarRetornoOauth(window.location.search, window.location.hash);
    if (situacao === 'sem-erro') return;

    setErro(
      situacao === 'escolha-da-pessoa'
        ? t('A entrada com o Google foi cancelada. Você pode tentar de novo ou entrar com e-mail e senha.')
        : t('A entrada com o Google não foi concluída. Tente de novo.'),
    );

    window.history.replaceState(null, '', window.location.pathname);
  }, []);
  const [enviando, setEnviando] = useState(false);

  /**
   * Ver a senha digitada. Linha L09 da paridade do prontidao, e entrou em 29/08/2026.
   *
   * NASCE OCULTA, e o motivo esta no proprio legado: a tela de entrada e usada em lugar com
   * gente em volta, e mostrar por padrao exporia a senha de quem nao pediu para ve-la. Quem
   * quer conferir o que digitou pede; quem nao pediu continua protegido.
   */
  const [mostrarSenha, setMostrarSenha] = useState(false);

  /**
   * O aviso de privacidade, ALCANCAVEL ANTES DE ENTRAR. Linha L10 da paridade.
   *
   * ESTA E A DIVERGENCIA MAIS CARA DAS OITO, e por isso ela nao ficou para depois: quem ainda
   * nao tem conta precisa poder ler o que o sistema faz com os dados dela ANTES de fornecer
   * qualquer um. Um aviso que so existe depois do login e um aviso lido depois de a pessoa ja
   * ter entregado o e-mail, e a LGPD espera a informacao antes da coleta, nao depois.
   */
  const [avisoAberto, setAvisoAberto] = useState(false);

  async function enviar(e: FormEvent) {
    e.preventDefault();

    /*
     * 🔴 A RECUSA ANTES DE IR AO SERVIDOR, e ate 03/09/2026 ela NAO existia.
     *
     * O QA de paridade mediu, e o achado tem tres partes que so juntas explicam o defeito:
     *   · os dois campos sao marcados `obrigatorio`, e o `Campo` traduz isso em `required`;
     *   · o `<form>` carrega `noValidate`, que DESLIGA a validacao do navegador;
     *   · esta funcao ia direto ao provedor, sem conferir nada.
     *
     * Resultado: a marca no rotulo nao valia nada. Enviar vazio gastava uma ida ao servidor
     * para receber "credenciais invalidas", que e uma mensagem sobre OUTRO problema: ela faz
     * a pessoa desconfiar da senha quando ela nem digitou.
     *
     * E O ITEM 9 DO BATIMENTO: obrigatorio e MARCADO **e** RECUSA envio vazio. Presenca sem
     * paridade e exatamente o que ele existe para pegar.
     *
     * A MENSAGEM DIZ QUAL CAMPO, e nao "preencha os campos": com dois campos a diferenca e
     * pequena, e a regra da casa e a mesma do item obrigatorio do checklist. Nomear custa o
     * mesmo e resolve na hora.
     *
     * `senha` NAO leva `trim`, e isso e deliberado: espaco pode fazer parte da senha, e
     * apara-lo aqui recusaria uma credencial legitima. O e-mail leva, porque espaco em volta
     * de e-mail e sempre engano de digitacao ou de colagem.
     */
    /*
     * O FORMATO do e-mail, e nao apenas o campo preenchido. Em 07/09/2026 o fundador
     * digitou a letra A aqui e o formulario seguiu, reclamando so da senha: a checagem
     * media se havia algo, e nunca se aquilo era um e-mail.
     *
     * A REGRA MORA EM packages/credenciais, camada 0, e nao neste arquivo: a mesma
     * exigencia precisa valer no backend, e servico nao importa camada 1 (front). Camada 0
     * e a unica que os dois lados alcancam.
     */
    const conferenciaEmail = validarEmail(email);
    if (!conferenciaEmail.ok) {
      setErro(conferenciaEmail.mensagem);
      return;
    }
    if (senha === '') {
      setErro('Informe a senha.');
      return;
    }

    setErro(null);
    setEnviando(true);
    try {
      const resultado = await provedor.signInWithPassword(email, senha);
      if (resultado.ok) aoEntrar(resultado);
      // A mensagem que chega ja vem em portugues e sem detalhe que ajude quem ataca:
      // a traducao acontece dentro do adaptador, e nao aqui.
      else setErro(resultado.mensagem);
    } finally {
      // No `finally` para o botao voltar mesmo quando a chamada lanca: sem isso, um erro
      // de rede deixaria o formulario travado em "entrando" para sempre.
      setEnviando(false);
    }
  }

  return (
    // A RAIZ E UMA COLUNA, e o mesmo desenho que o `AppShell` ja usava: a faixa de ambiente
    // e a primeira linha, e todo o resto e a segunda. Assim o espaco dela e reservado pelo
    // layout, e nao por uma altura compensada a mao em lugar nenhum.
    // O FUNDO E AFUNDADO, e o card e que carrega a superficie. Ele mandou em 08/09/2026,
    // com a captura da tela publicada e a do admin local lado a lado: "O EXEMPLO DA ADMIN,
    // NAO TEM CARD, ESTA OCUPANDO A TELA TODA". E logo depois fechou o alcance: "E TODOS OS
    // PROXIMOS QUE IRAO ENTRAR - VIROU NOSSO PADRAO DE LOGIN".
    //
    // POR QUE O TOKEN E NAO UMA COR: `superficie-afundada` ja inverte sozinho no tema
    // escuro, entao o card continua sendo a camada de cima nos dois temas. Um cinza literal
    // ficaria claro no escuro e o card sumiria dentro dele.
    <div className="relative flex min-h-screen flex-col bg-superficie-afundada">
      {/* O RASTRO DO CURSOR SAIU deste projeto, por decisao do fundador em 19/09/2026 ("tb nao
          vai ter o rastro do ponteiro do mouse"). Na plataforma ele e o primeiro filho desta
          raiz; aqui a tela nao o desenha. Divergencia protegida (`scripts/sincronizar-chassi.sh`). */}
      {/* O LOGIN E CHASSI, e nao uma tela de fora dele. Ate 30/08/2026 esta tela nao herdava
          nem a faixa nem os controles, entao quem chegava aqui nao sabia em que ambiente
          estava entrando e nao tinha como trocar para ingles ou para o tema escuro antes de
          entrar. Pedido dele no mesmo dia, olhando o desenho do chassi: "O LOGIN TB TEM QUE
          SER DA MESMA FORMA". */}
      {/* `relative z-10` poe a faixa NA FRENTE do rastro. Sem isto ela ficaria no mesmo
          plano do canvas, e o rastro passaria por cima do aviso de ambiente, que e
          justamente o que nao pode ficar ilegivel. */}
      <div className="relative z-10">
        <FaixaAmbiente ambiente={ambiente} />
      </div>

      {/* O RESPIRO EM VOLTA DO CARD, e ele cresce com a tela. `items-center` centraliza na
          vertical quando sobra altura, e `flex-1` faz esta faixa ocupar o que a faixa de
          ambiente deixou. No celular o padding cai para o minimo, porque ali cada pixel de
          largura e do conteudo. */}
      <div className="relative z-10 flex min-h-0 flex-1 items-center justify-center px-cx-4 py-cx-6 lg:px-cx-8 lg:py-cx-10">
        {/* O CARD.
            `overflow-hidden` NAO E ENFEITE: sem ele, o fundo da coluna de apresentacao
            (`superficie-elevada`) vaza por cima do canto arredondado e o card aparece com
            um quadrado colado no proprio canto.
            `max-w` limita a largura em tela grande: sem limite, as duas colunas esticam ate
            a borda e a linha de texto passa do comprimento que se le sem cansar. */}
        {/*
          `bg-superficie-elevada` E BORDA, e nao mais `bg-superficie` sem contorno.
          ============================================================================
          EU QUEBREI ESTE FRAME em 09/09/2026, e o registro fica: para atender o pedido dele
          ("NO MODO CLARO ESTA TUDO MUITO SEM APARECER O FRAME"), inverti `--cx-superficie`
          (255 -> 242) e `--cx-superficie-elevada` (250 -> 255), porque no padrao Apple a
          pagina AFUNDA e o cartao SOBE.

          Isso consertou os cartoes do app inteiro E QUEBROU O LOGIN, porque esta tela usava
          os tokens ao contrario: `superficie-afundada` (245) na pagina e `superficie` (era
          255, virou 242) no cartao. Depois da troca os dois ficaram a TRES niveis um do
          outro, e a varredura mediu 1.02:1: o cartao desapareceu na pagina.

          E ELE NAO TINHA BORDA NENHUMA para segurar o contorno quando a cor falhasse. Os
          cartoes do app tem `border-borda` e por isso sobreviveram; este dependia so da
          diferenca de cor.

          A LICAO, e ela vale para toda troca de token: mudar um token de superficie alcanca
          quem o usa do jeito ESPERADO e quebra quem o usa ao contrario. O que protege nao e
          lembrar de todos os usos: e cada superficie ter borda propria, para nunca depender
          de uma unica diferenca.
        */}
        <div className="w-full max-w-[1180px] overflow-hidden rounded-cx-xl border border-borda bg-superficie-elevada shadow-cx-lg">
          <div className="grid grid-cols-1 lg:grid-cols-2">
      {/* -------------------------------------------------------------------------
          COLUNA DE APRESENTACAO
          `order-2` abaixo de lg, e nao escondida. A decisao antiga de esconde-la vinha de
          uma premissa correta, que continua valendo: em telefone ela empurraria o formulario
          para baixo da dobra, e quem abre a tela de login quer entrar, nao ler. A ordem
          resolve isso sem apagar nada, e e por isso que ela substituiu o `hidden`: o
          formulario continua sendo a primeira coisa da tela, e quem quiser saber onde esta
          entrando rola e le.
          ------------------------------------------------------------------------- */}
      <aside
        // O `id` que existia aqui SAIU em 08/09/2026 junto com o link "Saiba mais", por
        // pedido dele. Ele nao era decoracao: era o destino da ancora, e sem o link virou
        // atributo que nao faz nada. No celular esta coluna continua DEPOIS do formulario
        // (`order-2`), entao quem abre a tela ve o campo de entrar primeiro e desce para
        // ler o que o sistema faz, agora rolando e nao clicando.
        className="order-2 flex flex-col justify-between gap-cx-8 bg-superficie-elevada p-cx-6 lg:order-none lg:gap-0 lg:p-cx-12"
      >
        <div className="hidden items-center gap-cx-3 lg:flex">
          {/* O SIMBOLO DA MARCA, e nao as iniciais do produto. Pedido dele em 29/08/2026,
              olhando a tela: as duas letras num quadrado azul liam como avatar de usuario,
              e nao como a marca da casa. O simbolo e o mesmo do site institucional, gerado
              do mesmo .svg, entao os dois nao podem divergir.

              A cor vem do `text-marca`: o desenho usa `currentColor`, entao ele acompanha
              tema claro e escuro sem duplicar arquivo. */}
          <span
            aria-hidden="true"
            className="flex h-10 w-10 items-center justify-center text-marca"
          >
            <Simbolo className="h-full w-full" />
          </span>
          <span className="text-cx-sm font-semibold text-texto">{produto.nomeCompleto ?? produto.nome}</span>
        </div>

        <div className="flex flex-col gap-cx-4">
          <h2 className="text-cx-2xl font-semibold text-texto lg:text-cx-3xl">{t(produto.login.titulo)}</h2>
          {/* O SUBTITULO COM PESO NAS PALAVRAS QUE IMPORTAM.
              Ele mandou em 08/09/2026, com a captura: "Desde o amador ao voo executivo" sai
              com as duas palavras em negrito, porque sao elas que dizem o alcance do
              produto. Produto que nao declarar `enfasesDoSubtitulo` desenha a frase inteira
              no mesmo peso, exatamente como antes. */}
          <p className="text-cx-base text-texto-suave lg:text-cx-lg">
            {comEnfase(
              t(produto.login.subtitulo),
              // AS PALAVRAS DA ENFASE TAMBEM PASSAM PELO DICIONARIO, e sem isto a enfase
              // MORRE EM SILENCIO no ingles: o subtitulo vira outra frase, e "amador" nao
              // existe dentro dela. Nada quebra, nada avisa, e a tela simplesmente perde o
              // peso nas duas palavras que dizem o alcance do produto.
              //
              // FUNCIONA PELO MESMO MECANISMO DO RESTO: a chave e a palavra em portugues, e
              // o produto cadastra a traducao dela no `en.ts` dele, ao lado da traducao do
              // proprio subtitulo. Sem cadastro, `t()` devolve o portugues, que e o
              // comportamento de hoje: nao pinta, e nao quebra.
              produto.login.enfasesDoSubtitulo?.map((palavra) => t(palavra)),
            )}
          </p>

          {produto.login.paragrafos.map((p) => (
            <p key={p} className="text-cx-sm text-texto-suave">
              {t(p)}
            </p>
          ))}

          {/* Produto sem IA nao desenha o bloco, em vez de desenha-lo vazio. */}
          {produto.login.blocoIA && (
            <div className="cx-cartao flex flex-col gap-cx-2 p-cx-4">
              <span className="cx-selo-ia">IA</span>
              <h3 className="text-cx-base font-semibold text-texto">
                {t(produto.login.blocoIA.titulo)}
              </h3>
              {/* A LISTA DE USOS DA IA, um por linha, com o tique a esquerda.
                  ELE MANDOU ASSIM em 07/09/2026, e o motivo se ve na propria tela: nove usos
                  num paragrafo unico se leem de uma ponta a outra, e ninguem le a tela de
                  entrada de ponta a ponta. Em lista, o olho varre e para no que interessa.

                  <ul> E NAO uma pilha de <div>: quem usa leitor de tela ouve "lista de nove
                  itens" antes do primeiro, e ai sabe o tamanho do que vem pela frente. Uma
                  pilha de divs entrega os mesmos textos sem essa informacao.

                  O TIQUE E `aria-hidden`, e o `role="list"` explicito acompanha: o `list-none`
                  do reset do Tailwind remove a semantica de lista no VoiceOver do Safari, e
                  esse e o defeito classico de lista estilizada. O atributo devolve o que a
                  regra de estilo tirou. */}
              {/* `gap-cx-1` E NAO `gap-cx-2`, por pedido dele em 08/09/2026 olhando a tela:
                  "DIMINIUIR O ESPACO ENTRE AS LINHAS NAS CITACOES DA IA". Nove itens curtos
                  com respiro de dois passos leem como nove blocos separados; com um passo
                  eles leem como UMA lista, que e o que a peca quer dizer. */}
              <ul role="list" className="flex flex-col gap-cx-1">
                {produto.login.blocoIA.itens.map((item) => {
                  // O DESENHO E RESOLVIDO PELO MAPA, e nao escrito aqui: emoji e caractere
                  // Unicode sao proibidos na plataforma, e o `check` do mapa herda a cor do
                  // texto em volta, entao ele acompanha os dois temas sem duplicar arquivo.
                  const Tique = resolverIcone('check');
                  return (
                    <li key={item} className="flex items-start gap-cx-2 text-cx-sm text-texto-suave">
                      {/* `mt-0.5` alinha o tique com a PRIMEIRA LINHA do texto, e nao com o
                          bloco inteiro: item que quebra em duas linhas centralizaria o tique
                          no meio das duas, e ai ele deixa de apontar para onde a frase comeca.
                          `shrink-0` impede o tique de ser espremido quando o texto e longo. */}
                      {Tique ? (
                        <Tique aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-marca" />
                      ) : null}
                      <span>{t(item)}</span>
                    </li>
                  );
                })}
              </ul>
              {/* O limite da IA aparece na PRIMEIRA tela do sistema, e nao escondido num
                  termo de uso. Se a tela de login nao disser, nenhuma tela dira. */}
              <p className="text-cx-xs text-texto-fraco">{t(produto.login.blocoIA.limite)}</p>
            </div>
          )}
        </div>

        {/* A marca da PLATAFORMA, e nao a do produto. O produto.nome e o produto.iniciais
            continuam vindo do ProductConfig, logo acima: sao duas identidades, e a de
            cima e a do produto. Trocar o wordmark em @complexity/brand muda esta linha
            sem tocar em nenhum arquivo daqui. */}
        <p className="flex flex-wrap items-center justify-center gap-cx-1 text-center text-cx-xs text-texto-fraco">
          {t('uma plataforma')}
          {/* O CONJUNTO INTEIRO E UM LINK, e nao so o nome. Pedido dele em 29/08/2026.
              O alvo unico evita o defeito classico de marca clicavel: simbolo e texto
              como dois links vizinhos criam duas paradas de teclado para o mesmo destino,
              e no celular dois alvos pequenos onde deveria haver um.

              O rotulo vive no proprio <a> porque o simbolo e `aria-hidden`: sem isto, quem
              usa leitor de tela ouviria so "Complexity Digital Systems, link", sem saber
              que ele leva para fora do sistema.

              O ALVO MINIMO E DESLIGADO POR PONTEIRO, e nao por LARGURA. Ate 30/08/2026
              era `lg:min-h-0`, e a varredura dos 5 apps mediu o link com 16px de altura de
              1024px para cima. Largura nao diz nada sobre ponteiro: um iPad Pro tem 1366px
              e e toque puro, entao a regra por largura desligava o alvo justamente num
              aparelho de dedo. `pointer-fine` pergunta o que interessa, que e se existe um
              cursor preciso apontando para a tela.

              `rel="noopener"` com `target="_blank"` nao e formalidade: sem ele a pagina
              aberta ganha referencia a esta pela `window.opener`. E abre em outra aba de
              proposito: quem esta na tela de login esta tentando ENTRAR, e trocar a aba
              por um site institucional perderia o que ela ja digitou. */}
          <a
            href={`https://${DOMINIO}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="marca-plataforma"
            aria-label={`${NOME_PARTES.primeira} ${NOME_PARTES.destaque}, ${t('abrir o site da plataforma em nova aba')}`}
            className="inline-flex min-h-toque items-center gap-cx-1 whitespace-nowrap rounded-cx-sm font-semibold text-texto hover:underline pointer-fine:min-h-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-marca"
          >
            <Simbolo className="h-cx-4 w-cx-4 shrink-0 text-marca" />
            {NOME_PARTES.primeira}{' '}
            <span className="text-marca">{NOME_PARTES.destaque}</span>
          </a>
        </p>

      </aside>

      {/* -------------------------------------------------------------------------
          COLUNA DO FORMULARIO
          Identica em todos os produtos, de proposito: e o que faz uma correcao de
          acessibilidade ou de seguranca aqui valer para o sistema inteiro.
          ------------------------------------------------------------------------- */}
      {/* A COLUNA DO FORMULARIO vira uma pilha: os controles em cima, o formulario embaixo.
          O `order-1` subiu do <main> para ca porque quem troca de lugar no celular agora e a
          pilha inteira, e nao so o formulario. */}
      <div className="order-1 flex min-w-0 flex-col lg:order-none">
        {/* IDIOMA E TEMA, no frame superior e a DIREITA, que e a regra da casa em todo
            produto. No FLUXO e nao por cima: posicionado por cima, em 390px ele disputaria
            o espaco com o simbolo do produto que abre o formulario. */}
        <div className="flex shrink-0 justify-end px-cx-4 pt-cx-3">
          <ControlesIdiomaTema />
        </div>

        <main className="flex flex-1 items-center justify-center px-cx-6 pb-cx-10 lg:pb-cx-6">
        <form onSubmit={enviar} className="flex w-full max-w-sm flex-col gap-cx-4" noValidate>
          {/* No celular a identidade do produto aparece aqui, ja que a coluna da esquerda
              nao existe naquela largura. Sem isso, a pessoa nao saberia em que sistema
              esta entrando. */}
          <div className="flex items-center gap-cx-2 lg:hidden">
            <span
              aria-hidden="true"
              className="flex h-9 w-9 items-center justify-center text-marca"
            >
              <Simbolo className="h-full w-full" />
            </span>
            <span className="text-cx-sm font-semibold text-texto">{produto.nomeCompleto ?? produto.nome}</span>
          </div>

          {/* O TITULO SAI DA TELA E FICA PARA QUEM OUVE.
              Na captura que ele enviou em 08/09/2026 nao ha a palavra "Entrar" como titulo:
              o texto de instrucao ocupa aquele lugar, e a tela fica mais limpa. Mas APAGAR
              o <h1> nao e opcao: ele e o cabecalho de nivel 1 da pagina, e sem ele quem
              navega por cabecalhos em leitor de tela nao acha o formulario. `sr-only` mantem
              a estrutura e tira o peso visual, que e exatamente o que ele pediu. */}
          <h1 className="sr-only">{t('Entrar')}</h1>

          {/* A INSTRUCAO DIZ OS DOIS CAMINHOS ANTES DE MOSTRA-LOS, e ela e do CHASSI: a
              frase vale para qualquer produto da plataforma, porque os dois caminhos sao os
              mesmos em todos. Ela tambem responde a pergunta que a tela de entrada de
              sistema fechado sempre levanta, e que e a causa mais comum de abandono: "eu
              tenho conta aqui?". A resposta e que o administrador cadastrou. */}
          <p className="text-cx-sm text-texto-suave">
            {t('Entre com a sua conta Google, ou com o e-mail e a senha que o administrador cadastrou para você.')}
          </p>

          {erro && (
            // role="alert" anuncia assim que a mensagem aparece. Sem isso, quem usa leitor
            // de tela so descobriria o erro ao voltar navegando ate ele.
            <p role="alert" className="rounded-cx-md bg-erro-suave px-cx-3 py-cx-2 text-cx-sm text-erro">
              {erro}
            </p>
          )}

          {/* O CAMINHO DE UM GESTO VEM PRIMEIRO, e o de digitar vem depois.
              Ordem da tela que ele aprovou em 08/09/2026, e ela tem razao de ser: entrar
              com Google custa um toque, e entrar com e-mail e senha custa lembrar e digitar
              duas coisas. Por o mais barato em cima e o padrao das plataformas que fazem
              isto bem, e o que estava aqui antes empurrava o Google para baixo do
              formulario inteiro, onde no celular ele ficava fora da primeira tela. */}
          <Botao
            variante="secundario"
            // A isca do legado, que a suite mede por ela. Sem o atributo, o teste mediria
            // "algum botao secundario", e passaria verde no dia em que outro aparecesse.
            data-testid="entrar-google"
            // So layout: `flex` e `gap` alinham o logotipo ao texto. Fonte, peso, altura e
            // cor continuam vindo da variante `secundario`, e nao sao tocadas aqui, porque
            // o pedido dele foi manter a fonte e SOMAR o logo, e nao redesenhar o botao.
            className="flex items-center justify-center gap-2"
            onClick={() => {
              void provedor.signInWithGoogle(urlRetorno);
            }}
          >
            {/* LOGOTIPO DO GOOGLE, que e PECA da plataforma e mora em
                `packages/ui/src/components/marcas/LogoGoogle.tsx`. Ele era markup inline
                aqui ate 31/08/2026, identico byte a byte ao da peca, e a duplicata era
                exatamente o que a premissa de fonte unica do ADR 0028 proibe.

                As quatro cores oficiais e o motivo de elas serem literais (a diretriz de
                marca do Google exige o simbolo dele, e nao um icone generico) estao
                escritos na peca, junto do `aria-hidden` e do porque de o SVG ser inline. */}
            <LogoGoogle />
            {t('Entrar com Google')}
          </Botao>

          {/*
            O CAMINHO DE DIGITAR, e ele so existe onde a conta de trabalho existe.

            Ver `entradaPorSenha`, no topo deste arquivo, para o pedido dele e o porque de a
            lista de ambientes ser a MESMA do provedor. Em producao sobra o botao do Google
            acima, que e exatamente o que ele pediu ao mandar a captura do Super Admin.

            O `<form>` FICA, com o divisor e os campos dentro desta condicao: o Enter no
            campo de senha depende dele, e tirar o formulario junto quebraria o gesto sem
            ninguem perceber, porque o clique no botao continuaria funcionando.
          */}
          {entradaPorSenha && (
            <>
          <div className="flex items-center gap-cx-3">
            <hr className="flex-1 border-borda" />
            <span className="text-cx-xs text-texto-fraco">{t('ou entre com e-mail e senha')}</span>
            <hr className="flex-1 border-borda" />
          </div>

          <Campo
            rotulo={t('E-mail')}
            type="email"
            name="email"
            autoComplete="email"
            obrigatorio
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          {/* O campo de senha e o botao de ver ficam no mesmo bloco relativo, e o botao e
              posicionado por cima da borda direita do campo. Poe-lo AO LADO empurraria o
              campo e quebraria o alinhamento com o de e-mail logo acima. */}
          <div className="relative">
            <Campo
              rotulo={t('Senha')}
              // O `type` E O ESTADO. Trocar entre password e text e o mecanismo inteiro:
              // qualquer coisa mais esperta (mascara propria, sobreposicao) quebra o
              // preenchimento automatico do gerenciador de senhas.
              type={mostrarSenha ? 'text' : 'password'}
              name="senha"
              // current-password e nao new-password: o gerenciador de senhas do navegador
              // usa isso para decidir entre preencher e sugerir uma senha nova.
              autoComplete="current-password"
              obrigatorio
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setMostrarSenha((v) => !v)}
              // O rotulo diz o que o clique VAI FAZER, e nao o estado atual. "Mostrar senha"
              // com a senha ja visivel deixaria quem usa leitor de tela sem saber o que
              // aconteceu ao apertar.
              aria-label={mostrarSenha ? t('Ocultar senha') : t('Mostrar senha')}
              // `aria-pressed` diz o estado, que e a outra metade da informacao. Icone
              // sozinho nao chega a quem nao enxerga o desenho.
              aria-pressed={mostrarSenha}
              /**
               * `top-7` desce o botao a altura da caixa, passando do rotulo que fica acima
               * dela. Alvo de 44px porque este botao e tocado com o polegar como qualquer
               * outro, e um alvo menor num campo de senha faz a pessoa tocar dentro do campo
               * por engano e mover o cursor no meio da digitacao.
               *
               * `min-h-toque min-w-toque`, E NAO `h-toque w-toque`. DEFEITO MEDIDO EM
               * 29/08/2026, NAO REPETIR: o preset define o alvo so como `minHeight` e
               * `minWidth` (`tailwind.preset.js:135-136`), entao `h-toque` e `w-toque` sao
               * classes que NAO EXISTEM. O Tailwind as ignora em silencio, sem erro e sem
               * aviso, e o botao encolheu para os 20px do proprio icone. A suite mediu 20px
               * onde o minimo e 44px, e foi ela que pegou: ler o arquivo mostraria a
               * intencao, e so a medicao no navegador mostra o que foi desenhado.
               */
              className="absolute right-cx-1 top-7 flex min-h-toque min-w-toque items-center justify-center rounded-cx-md text-texto-fraco hover:text-texto"
            >
              <svg
                viewBox="0 0 24 24"
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.6}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                {mostrarSenha ? (
                  // Olho cortado: o proximo clique volta a esconder.
                  <>
                    <path d="M3 3l18 18" />
                    <path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" />
                    <path d="M6.7 6.7C4.6 8 3 10 2 12c2 4 6 7 10 7 1.7 0 3.3-.5 4.7-1.3" />
                    <path d="M9.9 5.2A9.9 9.9 0 0 1 12 5c4 0 8 3 10 7a17 17 0 0 1-2.7 3.6" />
                  </>
                ) : (
                  // Olho aberto: o proximo clique mostra.
                  <>
                    <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7Z" />
                    <circle cx="12" cy="12" r="3" />
                  </>
                )}
              </svg>
            </button>
          </div>

          {/* type=submit e nao o padrao do componente: aqui o clique PRECISA enviar o
              formulario, para o Enter no campo de senha funcionar igual ao clique. */}
          <Botao type="submit" carregando={enviando}>
            {enviando ? t('Entrando...') : t('Entrar')}
          </Botao>
            </>
          )}


          {/* -----------------------------------------------------------------------
              O AVISO DE PRIVACIDADE, ALCANCAVEL ANTES DE ENTRAR. Linha L10 da paridade.

              Produto que nao declarou o proprio aviso NAO ganha este link, e isso e
              deliberado: link que abre uma tela em branco e pior que link nenhum, porque
              quem clica num link de privacidade e exatamente quem esta desconfiado, e a
              tela vazia confirma a desconfianca. O loader recusa um aviso declarado e vazio
              pelo mesmo motivo.

              Fica DENTRO do <form> e depois dos botoes: e onde a pessoa esta olhando quando
              hesita em entregar o e-mail. No rodape da pagina, ela precisaria rolar para
              baixo para achar, e quem hesita nao rola.
              ----------------------------------------------------------------------- */}
          {/* -----------------------------------------------------------------------
              OS SELOS DO PRODUTO, e eles MUDARAM DE COLUNA em 13/09/2026.

              PEDIDO DELE, com a captura do selo na tela: "no lado do login, antes de seus
              dados e a lgpd", e o alcance: "vira chassi".

              ONDE ELES ESTAVAM: no `aside`, o lado da promessa, fechando aquela coluna.
              Quem lia a promessa via os selos; quem ia direto ao formulario nao via nenhum.
              E em 390px as duas colunas viram duas tiras, entao o selo ficava longe do
              gesto que ele qualifica.

              ONDE ELES FICAM AGORA: ao lado do formulario, imediatamente ANTES do link da
              LGPD. Os dois dizem a mesma coisa em dois niveis: o selo AFIRMA o que o sistema
              garante, e o link ABRE o texto que explica como. Juntos, sao lidos no instante
              em que a pessoa hesita em entregar o e-mail, que e quando a duvida sobre
              confianca acontece.

              ISTO E CHASSI, e nao ajuste de um produto: os QUATRO produtos declaram selos
              (medico-concierge, admin, voe e _template), e todos ganham a posicao nova sem
              tocar em manifesto nenhum.
              ----------------------------------------------------------------------- */}
        <ul className="flex flex-wrap justify-between gap-cx-2">
          {produto.login.selos.map((s) => (
            <li
              key={s.texto}
              className="flex items-center gap-cx-1 whitespace-nowrap rounded-cx-full border border-borda px-cx-3 py-cx-1 text-cx-xs text-texto-suave"
            >
              {/* O SELO DESENHA O ICONE, e nao imprime o nome dele.
                  DEFEITO MEDIDO NA TELA EM 30/08/2026: esta linha era
                  `<span>{s.icone}</span>`, entao o selo saia escrito "conformidade
                  Conformidade RBAC, ICA e leis", com o nome do desenho vazando como texto na
                  frente do rotulo. E o MESMO buraco que o rail e a barra inferior fecharam
                  naquele dia, e que deixava o emoji chegar a tela; a tela de login tinha
                  ficado de fora dos dois.
                  NOME DESCONHECIDO NAO DESENHA NADA AQUI, e a diferenca para o rail e
                  proposital: la o ponto mantem a lista alinhada, e aqui o selo tem o proprio
                  texto ao lado, entao um ponto solto seria ruido sem informacao. */}
              {(() => {
                if (!s.icone) return null;
                const Desenho = resolverIcone(s.icone);
                return Desenho ? (
                  <Desenho aria-hidden="true" className="h-4 w-4 shrink-0" />
                ) : null;
              })()}
              {t(s.texto)}
            </li>
          ))}
        </ul>

          {produto.login.privacidade && (
            <button
              type="button"
              onClick={() => setAvisoAberto(true)}
              // A isca da linha L11: o legado identifica este botao assim, e a suite mede por
              // ele. Sem o atributo, a suite mediria "algum botao que abre o aviso", e passaria
              // verde no dia em que outro botao qualquer aparecesse ao lado.
              data-testid="selo-lgpd"
              className={
                produto.login.privacidade.selo
                  // COM selo: escudo mais rotulo curto, na cor da marca, sublinhado so no
                  // hover. E o tratamento do legado (PaginaEntrar.tsx:435-446), onde o aviso
                  // NAO e um link discreto: ele afirma cuidado com o dado, e por isso pesa.
                  ? 'flex min-h-toque w-fit items-center gap-1.5 rounded text-cx-xs font-medium text-marca underline-offset-2 hover:underline'
                  // SEM selo: exatamente o que estava aqui antes, para nenhum produto que nao
                  // declarou o campo ter a tela alterada por uma decisao que nao foi dele.
                  // `underline` porque isto e um link em tudo menos na tag: sublinhado e o unico
                  // sinal que sobrevive a quem nao distingue a cor do texto ao redor.
                  : 'min-h-toque text-cx-xs text-texto-fraco underline underline-offset-2 hover:text-texto'
              }
            >
              {produto.login.privacidade.selo && (
                // O escudo com o check, transcrito do legado. `aria-hidden` porque ele nao
                // acrescenta nada a quem ouve: o texto ao lado ja diz o que o botao faz, e
                // anunciar "imagem" no meio da frase so atrapalha.
                <svg
                  viewBox="0 0 24 24"
                  className="h-4 w-4 shrink-0"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  aria-hidden="true"
                >
                  <path d="M12 3 4 6v6c0 4.5 3.2 8.3 8 9 4.8-.7 8-4.5 8-9V6l-8-3Z" />
                  <path d="M9.5 12.2l1.8 1.8 3.4-3.4" />
                </svg>
              )}
              {t(produto.login.privacidade.selo ?? produto.login.privacidade.titulo)}
            </button>
          )}
        </form>
          </main>
          </div>
          </div>
        </div>
      </div>

      {/* RODAPE LEGAL · a linha da LGPD, fora das duas colunas.
          Ele mandou em 08/09/2026, com a captura da tela publicada, e decidiu em enquete
          que ela nasce no CHASSI e vale para todos os produtos: o texto da lei e identico
          em qualquer um deles, e escrito por produto envelheceria de um lado so.

          A SEGUNDA FRASE E CONDICIONAL, e isso nao e detalhe: ela aponta para o aviso de
          privacidade, e produto que nao declarou aviso nenhum estaria mandando a pessoa
          procurar um botao que nao existe. O ROTULO vem do proprio produto, e nao escrito
          aqui, pelo mesmo motivo: dois textos para a mesma coisa divergem no dia em que
          alguem muda um deles.

          FORA DO <form> e fora das colunas, porque ele fala da tela inteira e nao do ato
          de entrar. Dentro do formulario, um leitor de tela o anunciaria no meio dos
          campos, como se fosse instrucao de preenchimento. */}
      <p className="relative z-10 flex flex-wrap items-center justify-center gap-cx-1 px-cx-6 pb-cx-6 text-center text-cx-xs text-texto-fraco">
        {/* O ESCUDO E O MESMO DESENHO do selo de privacidade logo acima, e nao um segundo
            icone parecido: as duas linhas falam do mesmo assunto, e desenhos diferentes
            fariam parecer que sao dois. */}
        <svg
          aria-hidden="true"
          focusable="false"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.6}
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-cx-4 w-cx-4 shrink-0"
        >
          <path d="M12 3 5 6v5c0 4.2 2.9 8.1 7 9 4.1-.9 7-4.8 7-9V6l-7-3Z" />
        </svg>
        <span>
          {t('Dados pessoais tratados sob a LGPD (Lei 13.709/2018).')}
          {produto.login.privacidade
            ? ` ${t('O aviso de privacidade está acima, em')} "${t(
                produto.login.privacidade.selo ?? produto.login.privacidade.titulo,
              )}".`
            : ''}
        </span>
        <span aria-hidden="true">·</span>
        {/* O NOME CURTO SAI COM O `DS` NA COR DA MARCA, por regra dura da plataforma, e ele
            e INDIVISIVEL: quebrar entre "Complexity" e "DS" partiria o nome no unico lugar
            onde ele nao pode partir. */}
        <span className="whitespace-nowrap">
          {t('Desenvolvido por')}{' '}
          {/* O NOME E LINK para o site de quem desenvolveu, em nova aba, por pedido do fundador em
              19/09/2026. Divergencia protegida: na plataforma esta linha nao tem link. Mesmo padrao
              do link da marca acima: alvo de 44px no toque, e foco visivel. */}
          <a
            href={URL_AUTORIA}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="autoria"
            aria-label={`${NOME_CURTO_PARTES.primeira}${NOME_CURTO_PARTES.sigla}, ${t('abrir o site da plataforma em nova aba')}`}
            className="inline-flex min-h-toque items-center rounded-cx-sm underline underline-offset-2 hover:text-texto pointer-fine:min-h-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-marca"
          >
            {NOME_CURTO_PARTES.primeira}
            <span className="font-semibold text-marca">{NOME_CURTO_PARTES.sigla}</span>
          </a>
        </span>
      </p>

      {/* O modal do aviso, montado fora das duas colunas para cobrir a tela inteira em
          qualquer largura. Ele so existe quando o produto declarou o aviso. */}
      {produto.login.privacidade && (
        <Modal
          aberto={avisoAberto}
          aoFechar={() => setAvisoAberto(false)}
          titulo={t(produto.login.privacidade.titulo)}
        >
          <div className="flex flex-col gap-cx-4">
            {produto.login.privacidade.secoes.map((secao) => (
              <section key={secao.titulo} className="flex flex-col gap-cx-2">
                <h3 className="text-cx-base font-semibold text-texto">{t(secao.titulo)}</h3>
                {secao.paragrafos.map((paragrafo) => (
                  <p key={paragrafo} className="text-cx-sm leading-relaxed text-texto-suave">
                    {t(paragrafo)}
                  </p>
                ))}
              </section>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
}
